// Libs
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { SWRResponse } from 'swr';
// App
import { CATALOG_SHOPS_URL } from '../shop.constants';
import { useSWRAxiosGetRequest } from 'app-shared/lib/api/utils/swr-axios-request.hooks';
import { AxiosHookResponse, useAxiosGetRequest } from 'app-shared/lib/api/utils/axios-request.hooks';
import { SearchHookResponse } from 'app-shared/lib/api/utils/search-hook.interfaces';
import useSearch from 'app-shared/lib/api/utils/search-hook';
import { escapeApostrophes } from 'app-shared/lib/utils/search.utils';
import { API_COMMON_BASE_URL } from 'app-shared/lib/api/utils/api.constants';
import { ShopProductSummary } from '../../product';
import { GET_SHOP_PRODUCTS_MAX_PAGE_SIZE, PRODUCTS_URL } from './product.constants';

/**
 * Get shop products api
 */
export const getShopProducts = (
  id: number,
  shopProductCategoryId?: number,
  isSuggestedInBag?: boolean
): Promise<AxiosResponse<ShopProductSummary[]>> => {
  const url = `${API_COMMON_BASE_URL}/${CATALOG_SHOPS_URL}/${id}/${PRODUCTS_URL}`;
  const config: AxiosRequestConfig = {
    params: {
      shopProductCategoryId,
      isSuggestedInBag,
    },
  };

  return axios.get(url, config);
};

/**
 * Get all shop products api, in a single page
 */
export const getAllShopProducts = (id: number): Promise<AxiosResponse<ShopProductSummary[]>> => {
  const url = `${API_COMMON_BASE_URL}/${CATALOG_SHOPS_URL}/${id}/${PRODUCTS_URL}`;
  const config: AxiosRequestConfig = {
    params: {
      size: GET_SHOP_PRODUCTS_MAX_PAGE_SIZE,
    },
  };

  return axios.get(url, config);
};

/**
 * Get shop product categories api hook
 */
export const useGetShopProductsSuggestedInBag = (id: number): SWRResponse<ShopProductSummary[], Error> => {
  const urlOptional = id ? `${API_COMMON_BASE_URL}/${CATALOG_SHOPS_URL}/${id}/${PRODUCTS_URL}` : null;
  const config: AxiosRequestConfig = {
    params: {
      isSuggestedInBag: true,
    },
  };

  return useSWRAxiosGetRequest(urlOptional, { revalidateOnFocus: false }, config);
};

/**
 * Get shop products api hook
 */
export const useGetShopProducts = (id: number): AxiosHookResponse<void, ShopProductSummary[]> => {
  const urlOptional = id ? `${API_COMMON_BASE_URL}/${CATALOG_SHOPS_URL}/${id}/${PRODUCTS_URL}` : null;
  const config: AxiosRequestConfig = {
    params: {
      size: GET_SHOP_PRODUCTS_MAX_PAGE_SIZE,
    },
  };

  return useAxiosGetRequest(urlOptional, undefined, config);
};

/**
 * Search shop products api axios fetcher
 * @param keywords the keywords for search
 * @param id the shop id
 */
export const searchShopProductsFetcher = (
  keywords: string,
  shopId: number,
  shopProductCategoryId?: number
): Promise<AxiosResponse> => {
  const shopProductCategoryIdQueryPart =
    (shopProductCategoryId && `&shopProductCategoryId=${shopProductCategoryId}`) || '';
  const url = `${API_COMMON_BASE_URL}/${CATALOG_SHOPS_URL}/${shopId}/${PRODUCTS_URL}/search?keywords=${escapeApostrophes(
    keywords
  )}&limit=20${shopProductCategoryIdQueryPart}`;

  return axios.get(url);
};

export const useSearchShopProducts = (): SearchHookResponse<ShopProductSummary> => {
  return useSearch(searchShopProductsFetcher);
};
